import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useBranchScope } from '@/modules/auth/useAuth';
import { CustomerPetPicker } from '@/modules/doctor-pos/CustomerPetPicker';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';

import {
  APPOINTMENTS_QUERY,
  APPT_CONSULTING_ROOMS_QUERY,
  BOOK_APPOINTMENT_MUTATION,
  CANCEL_APPOINTMENT_MUTATION,
  CHECKIN_APPOINTMENT_MUTATION,
  COMPLETE_APPOINTMENT_MUTATION,
  CONFIRM_APPOINTMENT_MUTATION,
  NOSHOW_APPOINTMENT_MUTATION,
  RESCHEDULE_APPOINTMENT_MUTATION,
  START_APPOINTMENT_MUTATION,
  VETERINARIAN_USERS_QUERY,
  idempotencyContext,
} from './appointments.gql';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const KINDS = ['consultation', 'vaccination', 'follow_up', 'surgery', 'grooming', 'emergency', 'other'] as const;
const STATUSES = ['booked', 'confirmed', 'checked_in', 'in_progress', 'completed', 'no_show', 'cancelled', 'rescheduled'] as const;

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  booked: 'info',
  confirmed: 'info',
  checked_in: 'default',
  in_progress: 'warning',
  completed: 'success',
  no_show: 'destructive',
  cancelled: 'destructive',
  rescheduled: 'muted',
};

function todayStr(): string {
  // Local calendar date (the day window is built from local midnight; UTC date would skew
  // the "Today" view in the evening for a UTC-6 clinic).
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function dayWindowIso(date: string): { from: string; to: string } {
  const from = new Date(`${date}T00:00:00`);
  const to = new Date(from.getTime());
  to.setDate(to.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}
function hm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function errText(e: unknown, t: (k: string) => string): string {
  const raw = e instanceof ApolloError ? (e.graphQLErrors[0]?.message ?? e.message) : e instanceof Error ? e.message : 'error';
  if (raw.includes('appointment_overlap')) return t('appointments.error_overlap');
  return raw;
}

function BookDialog({ rooms, vets, onClose, onBooked }: { rooms: any[]; vets: any[]; onClose: () => void; onBooked: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [sel, setSel] = useState<{ customer_id: string | null; pet_id: string | null }>({ customer_id: null, pet_id: null });
  const [vet, setVet] = useState('');
  const [room, setRoom] = useState('');
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState('30');
  const [kind, setKind] = useState('consultation');
  const [reason, setReason] = useState('');
  const [allow, setAllow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [book, { loading }] = useMutation(BOOK_APPOINTMENT_MUTATION);

  const submit = async (): Promise<void> => {
    if (!sel.pet_id || !vet || when === '') return;
    setError(null);
    try {
      await book({
        variables: {
          input: {
            pet_id: sel.pet_id,
            veterinarian_id: vet,
            consulting_room_id: room || undefined,
            scheduled_start_at: new Date(when).toISOString(),
            duration_minutes: Number.parseInt(duration, 10) || 30,
            appointment_kind: kind,
            chief_complaint: reason.trim() || undefined,
            allow_overlap: allow || undefined,
          },
        },
        context: idempotencyContext(),
      });
      onBooked();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-lg space-y-3 overflow-y-auto rounded-lg border p-4 shadow-lg" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-base font-bold tracking-tight">{t('appointments.book_title')}</h2>
        {error ? <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">{error}</div> : null}
        <CustomerPetPicker customer_id={sel.customer_id} pet_id={sel.pet_id} onChange={setSel} />
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label>{t('appointments.vet')}</Label>
            <select className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm" value={vet} onChange={(e) => setVet(e.target.value)}>
              <option value="">—</option>
              {vets.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('appointments.room')}</Label>
            <select className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm" value={room} onChange={(e) => setRoom(e.target.value)}>
              <option value="">{t('appointments.room_none')}</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label>{t('appointments.start')}</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t('appointments.duration_min')}</Label>
            <Input type="number" min="5" step="5" value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t('appointments.kind')}</Label>
            <select className="border-input bg-background h-10 w-full rounded-md border px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => <option key={k} value={k}>{t(`appointments.kind_${k}`)}</option>)}
            </select>
          </div>
          <label className="flex items-end gap-2 text-xs">
            <input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} />
            <span>{t('appointments.allow_overlap')}</span>
          </label>
        </div>
        <div className="space-y-1">
          <Label>{t('appointments.reason')}</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!sel.pet_id || !vet || when === '' || loading} onClick={() => void submit()}>{t('appointments.book')}</Button>
        </div>
      </div>
    </div>
  );
}

function RescheduleDialog({ appt, onClose, onDone }: { appt: any; onClose: () => void; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState(String(appt.duration_minutes));
  const [reason, setReason] = useState('');
  const [allow, setAllow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resched, { loading }] = useMutation(RESCHEDULE_APPOINTMENT_MUTATION);

  const submit = async (): Promise<void> => {
    if (when === '') return;
    setError(null);
    try {
      await resched({
        variables: { input: { appointment_id: appt.id, scheduled_start_at: new Date(when).toISOString(), duration_minutes: Number.parseInt(duration, 10) || appt.duration_minutes, allow_overlap: allow || undefined, reason: reason.trim() || undefined } },
        context: idempotencyContext(),
      });
      onDone();
    } catch (e) {
      setError(errText(e, t));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-sm space-y-3 rounded-lg border p-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-base font-bold tracking-tight">{t('appointments.reschedule_title')} · {appt.folio_number}</h2>
        {error ? <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">{error}</div> : null}
        <div className="space-y-1"><Label>{t('appointments.start')}</Label><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
        <div className="space-y-1"><Label>{t('appointments.duration_min')}</Label><Input type="number" min="5" step="5" value={duration} onChange={(e) => setDuration(e.target.value)} /></div>
        <div className="space-y-1"><Label>{t('appointments.reason')}</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} /><span>{t('appointments.allow_overlap')}</span></label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={when === '' || loading} onClick={() => void submit()}>{t('appointments.reschedule')}</Button>
        </div>
      </div>
    </div>
  );
}

export function AppointmentsPage(): JSX.Element {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayStr());
  const [vetFilter, setVetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showBook, setShowBook] = useState(false);
  const [reschedule, setReschedule] = useState<any | null>(null);
  const branch_id = useBranchScope();

  const win = useMemo(() => dayWindowIso(date), [date]);
  const { data, refetch } = useQuery(APPOINTMENTS_QUERY, {
    variables: { from: win.from, to: win.to, veterinarian_id: vetFilter || null, status: statusFilter || null, branch_id },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
  const { data: vetData } = useQuery(VETERINARIAN_USERS_QUERY, { fetchPolicy: 'cache-first' });
  const { data: roomData } = useQuery(APPT_CONSULTING_ROOMS_QUERY, { fetchPolicy: 'cache-first' });
  const appts: any[] = data?.appointments ?? [];
  const vets: any[] = vetData?.veterinarianUsers ?? [];
  const rooms: any[] = roomData?.consultingRooms?.items ?? [];
  const vetName = (id: string): string => vets.find((v) => v.id === id)?.full_name ?? '—';
  const roomName = (id: string | null): string => (id ? (rooms.find((r) => r.id === id)?.name ?? '—') : '—');

  const [confirm] = useMutation(CONFIRM_APPOINTMENT_MUTATION);
  const [checkIn] = useMutation(CHECKIN_APPOINTMENT_MUTATION);
  const [startA] = useMutation(START_APPOINTMENT_MUTATION);
  const [completeA] = useMutation(COMPLETE_APPOINTMENT_MUTATION);
  const [noShow] = useMutation(NOSHOW_APPOINTMENT_MUTATION);
  const [cancelA] = useMutation(CANCEL_APPOINTMENT_MUTATION);

  const run = async (fn: () => Promise<unknown>, msg: string): Promise<void> => {
    try {
      await fn();
      await refetch();
      showToast({ tone: 'success', title: msg });
    } catch (e) {
      showToast({ tone: 'error', title: errText(e, t) });
    }
  };
  const idv = (appointment_id: string): any => ({ variables: { appointment_id }, context: idempotencyContext() });

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-lg font-extrabold tracking-tight">{t('appointments.title')}</h1>
        <Button size="sm" onClick={() => setShowBook(true)}>{t('appointments.new')}</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setDate(shiftDay(date, -1))}>‹ {t('appointments.prev')}</Button>
        <Button size="sm" variant="outline" onClick={() => setDate(todayStr())}>{t('appointments.today')}</Button>
        <Button size="sm" variant="outline" onClick={() => setDate(shiftDay(date, 1))}>{t('appointments.next')} ›</Button>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" />
        <select className="border-input bg-background h-9 rounded-md border px-2 text-sm" value={vetFilter} onChange={(e) => setVetFilter(e.target.value)}>
          <option value="">{t('appointments.filter_vet')}</option>
          {vets.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
        </select>
        <select className="border-input bg-background h-9 rounded-md border px-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('appointments.filter_status')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`appointments.status_${s}`)}</option>)}
        </select>
      </div>

      {appts.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
          <p className="text-muted-foreground text-sm">{t('appointments.no_appts')}</p>
        </div>
      ) : (
        <ul className="space-y-1">
          {appts.map((a) => {
            const locked = ['completed', 'no_show', 'cancelled', 'rescheduled'].includes(a.status);
            return (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono tabular-nums">{hm(a.scheduled_start_at)}–{hm(a.scheduled_end_at)}</span>
                  <span className="font-medium">{a.pet_name ?? '—'}</span>
                  <span className="text-muted-foreground">{vetName(a.veterinarian_id)} · {roomName(a.consulting_room_id)} · {t(`appointments.kind_${a.appointment_kind}`)}</span>
                  <Badge variant={STATUS_VARIANT[a.status] ?? 'muted'}>{t(`appointments.status_${a.status}`)}</Badge>
                </div>
                {!locked ? (
                  <div className="flex flex-wrap gap-1">
                    {a.status === 'booked' ? <Button size="sm" variant="outline" onClick={() => void run(() => confirm(idv(a.id)), t('appointments.toast_confirmed'))}>{t('appointments.action_confirm')}</Button> : null}
                    {(a.status === 'booked' || a.status === 'confirmed') ? <Button size="sm" variant="outline" onClick={() => void run(() => checkIn(idv(a.id)), t('appointments.toast_checked_in'))}>{t('appointments.action_checkin')}</Button> : null}
                    {a.status === 'checked_in' ? (
                      a.sale_order_id ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/app/cashier?citas=1&order=${a.sale_order_id}`}>{t('appointments.action_go_to_cashier')}</Link>
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => void run(() => startA(idv(a.id)), t('appointments.toast_started'))}>{t('appointments.action_start')}</Button>
                      )
                    ) : null}
                    {a.status === 'in_progress' ? (
                      a.sale_order_id ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/app/cashier?citas=1&order=${a.sale_order_id}`}>{t('appointments.action_go_to_cashier')}</Link>
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => void run(() => completeA(idv(a.id)), t('appointments.toast_completed'))}>{t('appointments.action_complete')}</Button>
                      )
                    ) : null}
                    {(a.status === 'booked' || a.status === 'confirmed') ? <Button size="sm" variant="ghost" onClick={() => void run(() => noShow(idv(a.id)), t('appointments.toast_no_show'))}>{t('appointments.action_noshow')}</Button> : null}
                    {(a.status === 'booked' || a.status === 'confirmed') ? <Button size="sm" variant="ghost" onClick={() => setReschedule(a)}>{t('appointments.action_reschedule')}</Button> : null}
                    {(a.status === 'booked' || a.status === 'confirmed' || a.status === 'checked_in') ? <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(t('appointments.cancel_confirm'))) void run(() => cancelA({ variables: { input: { appointment_id: a.id } }, context: idempotencyContext() }), t('appointments.toast_cancelled')); }}>{t('appointments.action_cancel')}</Button> : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {showBook ? <BookDialog rooms={rooms} vets={vets} onClose={() => setShowBook(false)} onBooked={() => { setShowBook(false); void refetch(); showToast({ tone: 'success', title: t('appointments.toast_booked') }); }} /> : null}
      {reschedule ? <RescheduleDialog appt={reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); void refetch(); showToast({ tone: 'success', title: t('appointments.toast_rescheduled') }); }} /> : null}
    </div>
  );
}
